import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, finalize, of, shareReplay, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { Company } from '../shared/model/company.model';

export interface CompanyPayload {
  abbreviation: string;
  name: string;
  color: string;
}

@Injectable({ providedIn: 'root' })
export class CompanyService {
  private readonly http = inject(HttpClient);
  private readonly endpointUri = environment.apiConfig.uri + 'api/companies';

  private readonly _companies = signal<Company[]>([]);
  private readonly _byAbbreviation = computed(() => {
    const map = new Map<string, Company>();
    for (const c of this._companies()) {
      map.set(c.abbreviation, c);
    }
    return map;
  });
  private readonly _byId = computed(() => {
    const map = new Map<string, Company>();
    for (const c of this._companies()) {
      map.set(c.id, c);
    }
    return map;
  });

  private loaded = false;
  private inflight?: Observable<Company[]>;

  readonly companies = this._companies.asReadonly();

  load(): Observable<Company[]> {
    if (this.loaded) {
      return of(this._companies());
    }
    if (this.inflight) {
      return this.inflight;
    }
    this.inflight = this.http.get<Company[]>(this.endpointUri).pipe(
      tap(cs => {
        this._companies.set(cs);
        this.loaded = true;
      }),
      finalize(() => (this.inflight = undefined)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    return this.inflight;
  }

  reload(): Observable<Company[]> {
    this.loaded = false;
    return this.load();
  }

  create(payload: CompanyPayload): Observable<Company> {
    return this.http.post<Company>(this.endpointUri, payload).pipe(
      tap(created => this._companies.update(arr => [...arr, created])),
    );
  }

  update(id: string, payload: Partial<CompanyPayload>): Observable<Company> {
    return this.http.patch<Company>(`${this.endpointUri}/${id}`, payload).pipe(
      tap(updated => this._companies.update(arr => arr.map(c => (c.id === id ? updated : c)))),
    );
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.endpointUri}/${id}`).pipe(
      tap(() => this._companies.update(arr => arr.filter(c => c.id !== id))),
    );
  }

  byAbbreviation(abbreviation: string | null | undefined): Company | undefined {
    return abbreviation ? this._byAbbreviation().get(abbreviation) : undefined;
  }

  byId(id: string | null | undefined): Company | undefined {
    return id ? this._byId().get(id) : undefined;
  }

  /** Normalizes any incoming company reference (id or abbreviation) to the abbreviation, or null. */
  resolveAbbreviation(ref: string | null | undefined): string | null {
    if (!ref) return null;
    const trimmed = ref.trim();
    if (!trimmed) return null;
    const byId = this._byId().get(trimmed);
    if (byId) return byId.abbreviation;
    const byAbbr = this._byAbbreviation().get(trimmed);
    if (byAbbr) return byAbbr.abbreviation;
    const upper = trimmed.toUpperCase();
    for (const c of this._companies()) {
      if (c.abbreviation?.toUpperCase() === upper) return c.abbreviation;
    }
    return null;
  }
}
